import type {
  CatalogHardwareDetails,
  CatalogLightingDetails,
  CreateCatalogHardwareRequest,
  CreateCatalogLightingRequest,
  CreateDrawerSystemRequest,
  CreateDrawerSystemVariantRequest,
  CreateEdgeRequest,
  CreateMaterialRequest,
  DrawerSystemDetails,
  DrawerSystemVariantDetails,
  EdgeDetails,
  ImportDrawerSystemsResponse,
  ImportEdgesResponse,
  ImportHardwareResponse,
  ImportLightingResponse,
  ImportMaterialsResponse,
  ListCatalogHardwareQuery,
  ListCatalogHardwareResponse,
  ListCatalogLightingQuery,
  ListCatalogLightingResponse,
  DrawerVariantFacets,
  DrawerVariantFacetsQuery,
  ListDrawerSystemVariantsQuery,
  ListDrawerSystemVariantsResponse,
  ListDrawerSystemsQuery,
  ListDrawerSystemsResponse,
  ListEdgesQuery,
  ListEdgesResponse,
  ListMaterialsQuery,
  ListMaterialsResponse,
  MaterialDetails,
  PriceImpactReport,
  RecomputeImpactResponse,
  UpdateCatalogHardwareRequest,
  UpdateCatalogLightingRequest,
  UpdateDrawerSystemRequest,
  UpdateDrawerSystemVariantRequest,
  UpdateEdgeRequest,
  UpdateMaterialRequest,
} from '@kreart/shared';
import { API_BASE_URL, ApiError, api } from '@/lib/api';
import { authStore } from '@/features/auth/store';

function serialiseMaterials(q: ListMaterialsQuery): string {
  const p = new URLSearchParams();
  if (q.page !== 1) p.set('page', String(q.page));
  if (q.pageSize !== 25) p.set('pageSize', String(q.pageSize));
  if (q.search) p.set('search', q.search);
  if (q.category) p.set('category', q.category);
  if (q.usageScope) p.set('usageScope', q.usageScope);
  if (q.manufacturer) p.set('manufacturer', q.manufacturer);
  if (q.active !== undefined) p.set('active', String(q.active));
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

function serialiseEdges(q: ListEdgesQuery): string {
  const p = new URLSearchParams();
  if (q.page !== 1) p.set('page', String(q.page));
  if (q.pageSize !== 25) p.set('pageSize', String(q.pageSize));
  if (q.search) p.set('search', q.search);
  if (q.manufacturer) p.set('manufacturer', q.manufacturer);
  if (q.structure) p.set('structure', q.structure);
  if (q.active !== undefined) p.set('active', String(q.active));
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Specialised fetch for multipart upload. The shared `apiFetch` helper assumes
 * JSON bodies, so we reach for `fetch` directly here while still attaching the
 * bearer token + handling the JSON error envelope.
 */
async function uploadXlsx<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append('file', file);
  const token = authStore.snapshot.accessToken;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const ct = res.headers.get('content-type') ?? '';
  const payload = ct.includes('application/json') ? await res.json() : await res.text();
  if (res.ok) return payload as T;
  const env =
    typeof payload === 'object' && payload !== null
      ? (payload as { code?: string; message?: string; details?: unknown; requestId?: string })
      : { message: typeof payload === 'string' ? payload : 'Upload failed' };
  throw new ApiError(res.status, {
    code: env.code,
    message: env.message ?? `HTTP ${res.status}`,
    details: env.details,
    requestId: env.requestId,
  });
}

async function downloadXlsx(path: string, filename: string): Promise<void> {
  const token = authStore.snapshot.accessToken;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(res.status, { message: `Download failed (HTTP ${res.status})` });
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const materialsApi = {
  list: (query: ListMaterialsQuery) =>
    api.get<ListMaterialsResponse>(`/admin/catalog/materials${serialiseMaterials(query)}`),
  get: (id: string) => api.get<MaterialDetails>(`/admin/catalog/materials/${id}`),
  create: (body: CreateMaterialRequest) =>
    api.post<MaterialDetails>('/admin/catalog/materials', body),
  update: (id: string, body: UpdateMaterialRequest) =>
    api.patch<MaterialDetails>(`/admin/catalog/materials/${id}`, body),
  remove: (id: string) => api.delete<void>(`/admin/catalog/materials/${id}`),
  importDryRun: (file: File) =>
    uploadXlsx<ImportMaterialsResponse>('/admin/catalog/materials/import?dryRun=true', file),
  importApply: (file: File) =>
    uploadXlsx<ImportMaterialsResponse>('/admin/catalog/materials/import?dryRun=false', file),
  exportXlsx: () => downloadXlsx('/admin/catalog/materials/export', 'materials.xlsx'),
  getImpact: (id: string) => api.get<PriceImpactReport>(`/admin/catalog/materials/${id}/impact`),
  recomputeImpact: (id: string) =>
    api.post<RecomputeImpactResponse>(`/admin/catalog/materials/${id}/recompute`, {}),
};

export const edgesApi = {
  list: (query: ListEdgesQuery) =>
    api.get<ListEdgesResponse>(`/admin/catalog/edges${serialiseEdges(query)}`),
  get: (id: string) => api.get<EdgeDetails>(`/admin/catalog/edges/${id}`),
  create: (body: CreateEdgeRequest) => api.post<EdgeDetails>('/admin/catalog/edges', body),
  update: (id: string, body: UpdateEdgeRequest) =>
    api.patch<EdgeDetails>(`/admin/catalog/edges/${id}`, body),
  remove: (id: string) => api.delete<void>(`/admin/catalog/edges/${id}`),
  importDryRun: (file: File) =>
    uploadXlsx<ImportEdgesResponse>('/admin/catalog/edges/import?dryRun=true', file),
  importApply: (file: File) =>
    uploadXlsx<ImportEdgesResponse>('/admin/catalog/edges/import?dryRun=false', file),
  exportXlsx: () => downloadXlsx('/admin/catalog/edges/export', 'edges.xlsx'),
  getImpact: (id: string) => api.get<PriceImpactReport>(`/admin/catalog/edges/${id}/impact`),
  recomputeImpact: (id: string) =>
    api.post<RecomputeImpactResponse>(`/admin/catalog/edges/${id}/recompute`, {}),
};

function serialiseDrawerSystems(q: ListDrawerSystemsQuery): string {
  const p = new URLSearchParams();
  if (q.page !== 1) p.set('page', String(q.page));
  if (q.pageSize !== 25) p.set('pageSize', String(q.pageSize));
  if (q.search) p.set('search', q.search);
  if (q.manufacturer) p.set('manufacturer', q.manufacturer);
  if (q.active !== undefined) p.set('active', String(q.active));
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Drawer facets are multi-valued, so each selected value gets its own repeated
 * query param. An empty selection is simply omitted, which the API reads as
 * "no constraint on this field".
 */
function appendDrawerFacets(p: URLSearchParams, q: DrawerVariantFacetsQuery): void {
  if (q.search) p.set('search', q.search);
  if (q.active !== undefined) p.set('active', String(q.active));
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  for (const value of q.modelSertar ?? []) p.append('modelSertar', value);
  for (const value of q.tipSertar ?? []) p.append('tipSertar', value);
  for (const value of q.descriere ?? []) p.append('descriere', value);
  for (const value of q.detaliu ?? []) p.append('detaliu', value);
  for (const value of q.hBox ?? []) p.append('hBox', value);
  for (const value of q.color ?? []) p.append('color', value);
  for (const value of q.depthMm ?? []) p.append('depthMm', String(value));
  if (q.nearDepthMm) p.set('nearDepthMm', String(q.nearDepthMm));
}

function serialiseDrawerSystemVariants(q: ListDrawerSystemVariantsQuery): string {
  const p = new URLSearchParams();
  if (q.page !== 1) p.set('page', String(q.page));
  if (q.pageSize !== 25) p.set('pageSize', String(q.pageSize));
  appendDrawerFacets(p, q);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

function serialiseDrawerVariantFacets(q: DrawerVariantFacetsQuery): string {
  const p = new URLSearchParams();
  appendDrawerFacets(p, q);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

function serialiseHardware(q: ListCatalogHardwareQuery): string {
  const p = new URLSearchParams();
  if (q.page !== 1) p.set('page', String(q.page));
  if (q.pageSize !== 25) p.set('pageSize', String(q.pageSize));
  if (q.search) p.set('search', q.search);
  if (q.manufacturer) p.set('manufacturer', q.manufacturer);
  if (q.active !== undefined) p.set('active', String(q.active));
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

function serialiseLighting(q: ListCatalogLightingQuery): string {
  const p = new URLSearchParams();
  if (q.page !== 1) p.set('page', String(q.page));
  if (q.pageSize !== 25) p.set('pageSize', String(q.pageSize));
  if (q.search) p.set('search', q.search);
  if (q.manufacturer) p.set('manufacturer', q.manufacturer);
  if (q.active !== undefined) p.set('active', String(q.active));
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

export const drawerSystemsApi = {
  list: (query: ListDrawerSystemsQuery) =>
    api.get<ListDrawerSystemsResponse>(
      `/admin/catalog/drawer-systems${serialiseDrawerSystems(query)}`,
    ),
  get: (id: string) => api.get<DrawerSystemDetails>(`/admin/catalog/drawer-systems/${id}`),
  searchVariants: (query: ListDrawerSystemVariantsQuery) =>
    api.get<ListDrawerSystemVariantsResponse>(
      `/admin/catalog/drawer-systems/variants/search${serialiseDrawerSystemVariants(query)}`,
    ),
  variantFacets: (query: DrawerVariantFacetsQuery) =>
    api.get<DrawerVariantFacets>(
      `/admin/catalog/drawer-systems/variants/facets${serialiseDrawerVariantFacets(query)}`,
    ),
  create: (body: CreateDrawerSystemRequest) =>
    api.post<DrawerSystemDetails>('/admin/catalog/drawer-systems', body),
  update: (id: string, body: UpdateDrawerSystemRequest) =>
    api.patch<DrawerSystemDetails>(`/admin/catalog/drawer-systems/${id}`, body),
  remove: (id: string) => api.delete<void>(`/admin/catalog/drawer-systems/${id}`),
  /**
   * A variant by its own id. The drawer rows that point at one store only
   * `drawerSystemVariantId`, so this is how a hydrated drawer finds out what
   * markup its slide system defaults to; the response carries `systemId` for
   * the write that may follow.
   */
  getVariantById: (variantId: string) =>
    api.get<DrawerSystemVariantDetails>(`/admin/catalog/drawer-systems/variants/${variantId}`),
  createVariant: (systemId: string, body: CreateDrawerSystemVariantRequest) =>
    api.post<DrawerSystemVariantDetails>(
      `/admin/catalog/drawer-systems/${systemId}/variants`,
      body,
    ),
  updateVariant: (systemId: string, variantId: string, body: UpdateDrawerSystemVariantRequest) =>
    api.patch<DrawerSystemVariantDetails>(
      `/admin/catalog/drawer-systems/${systemId}/variants/${variantId}`,
      body,
    ),
  removeVariant: (systemId: string, variantId: string) =>
    api.delete<void>(`/admin/catalog/drawer-systems/${systemId}/variants/${variantId}`),
  importDryRun: (file: File) =>
    uploadXlsx<ImportDrawerSystemsResponse>(
      '/admin/catalog/drawer-systems/import?dryRun=true',
      file,
    ),
  importApply: (file: File) =>
    uploadXlsx<ImportDrawerSystemsResponse>(
      '/admin/catalog/drawer-systems/import?dryRun=false',
      file,
    ),
  exportXlsx: () => downloadXlsx('/admin/catalog/drawer-systems/export', 'drawer-systems.xlsx'),
  getVariantImpact: (systemId: string, variantId: string) =>
    api.get<PriceImpactReport>(
      `/admin/catalog/drawer-systems/${systemId}/variants/${variantId}/impact`,
    ),
  recomputeVariantImpact: (systemId: string, variantId: string) =>
    api.post<RecomputeImpactResponse>(
      `/admin/catalog/drawer-systems/${systemId}/variants/${variantId}/recompute`,
      {},
    ),
};

export const hardwareApi = {
  list: (query: ListCatalogHardwareQuery) =>
    api.get<ListCatalogHardwareResponse>(`/admin/catalog/hardware${serialiseHardware(query)}`),
  get: (id: string) => api.get<CatalogHardwareDetails>(`/admin/catalog/hardware/${id}`),
  create: (body: CreateCatalogHardwareRequest) =>
    api.post<CatalogHardwareDetails>('/admin/catalog/hardware', body),
  update: (id: string, body: UpdateCatalogHardwareRequest) =>
    api.patch<CatalogHardwareDetails>(`/admin/catalog/hardware/${id}`, body),
  remove: (id: string) => api.delete<void>(`/admin/catalog/hardware/${id}`),
  importDryRun: (file: File) =>
    uploadXlsx<ImportHardwareResponse>('/admin/catalog/hardware/import?dryRun=true', file),
  importApply: (file: File) =>
    uploadXlsx<ImportHardwareResponse>('/admin/catalog/hardware/import?dryRun=false', file),
  exportXlsx: () => downloadXlsx('/admin/catalog/hardware/export', 'hardware.xlsx'),
  getImpact: (id: string) => api.get<PriceImpactReport>(`/admin/catalog/hardware/${id}/impact`),
  recomputeImpact: (id: string) =>
    api.post<RecomputeImpactResponse>(`/admin/catalog/hardware/${id}/recompute`, {}),
};

export const lightingApi = {
  list: (query: ListCatalogLightingQuery) =>
    api.get<ListCatalogLightingResponse>(`/admin/catalog/lighting${serialiseLighting(query)}`),
  get: (id: string) => api.get<CatalogLightingDetails>(`/admin/catalog/lighting/${id}`),
  create: (body: CreateCatalogLightingRequest) =>
    api.post<CatalogLightingDetails>('/admin/catalog/lighting', body),
  update: (id: string, body: UpdateCatalogLightingRequest) =>
    api.patch<CatalogLightingDetails>(`/admin/catalog/lighting/${id}`, body),
  remove: (id: string) => api.delete<void>(`/admin/catalog/lighting/${id}`),
  importDryRun: (file: File) =>
    uploadXlsx<ImportLightingResponse>('/admin/catalog/lighting/import?dryRun=true', file),
  importApply: (file: File) =>
    uploadXlsx<ImportLightingResponse>('/admin/catalog/lighting/import?dryRun=false', file),
  exportXlsx: () => downloadXlsx('/admin/catalog/lighting/export', 'lighting.xlsx'),
  getImpact: (id: string) => api.get<PriceImpactReport>(`/admin/catalog/lighting/${id}/impact`),
  recomputeImpact: (id: string) =>
    api.post<RecomputeImpactResponse>(`/admin/catalog/lighting/${id}/recompute`, {}),
};
